/**
 * `useBoxes` — fetches the current boxes snapshot and re-polls every
 * `pollIntervalSeconds` from runtime config. Cleans up its interval on
 * unmount; re-fetches on tab visibility change.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "../contexts/AuthContext";
import { configService } from "../services/configService";
import { fetchBoxes, MttrApiError } from "../services/mttrApi";
import type { BoxesSnapshot } from "../types/boxes";

export interface UseBoxesResult {
  snapshot: BoxesSnapshot | null;
  loading: boolean;
  error: Error | null;
  /** Manual refresh trigger — useful for an ops "refresh now" button. */
  refresh: () => Promise<void>;
  /** Last successful fetch time (Date), or null. */
  lastFetchedAt: Date | null;
}

export function useBoxes(): UseBoxesResult {
  const { token } = useAuth();
  const [snapshot, setSnapshot] = useState<BoxesSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
  const intervalRef = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    if (!token) {
      // Not signed in yet -- /api/boxes requires auth. Wait for the token.
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      // Every role hits /api/boxes; the server projects the payload per role
      // (platform gets the full operator view, customer/vendor gets a
      // trimmed projection) and scopes SCUs via vw_user_scu_access.
      const data = await fetchBoxes(token);
      setSnapshot(data);
      setLastFetchedAt(new Date());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new MttrApiError(String(e)));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Initial fetch
    void refresh();

    // Re-poll on the configured interval. We capture the value at mount —
    // changing pollIntervalSeconds at runtime requires a page reload, which
    // is fine for an internal status board.
    const cfg = configService.getConfig();
    const ms = Math.max(5, cfg.mttr.pollIntervalSeconds) * 1000;
    intervalRef.current = window.setInterval(() => {
      void refresh();
    }, ms);

    // Refresh immediately when the tab becomes visible after being hidden;
    // staleness from sleeping browsers is a real annoyance otherwise.
    const onVisibility = (): void => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refresh]);

  return { snapshot, loading, error, refresh, lastFetchedAt };
}
